import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Globe, Send } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useI18n } from '../contexts/I18nContext';

const ForumNewPost = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [languageCode, setLanguageCode] = useState('');
  const [languageName, setLanguageName] = useState('');
  const [languages, setLanguages] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const categoryNames = {
    'languages': t('forum.categories.languages'),
    'platform': t('forum.categories.platform'),
    'issues': t('forum.categories.issues'),
    'misc': t('forum.categories.misc')
  };

  useEffect(() => {
    if (categoryId === 'languages') {
      fetchLanguages();
    }
  }, [categoryId]);

  const fetchLanguages = async () => {
    try {
      const response = await api.get('/languages');
      const languageList = Array.isArray(response.data) ? response.data : (response.data.languages || []);
      setLanguages(languageList);
    } catch (err) {
      console.error('Failed to fetch languages:', err);
    }
  };

  const getWordCount = (text) => {
    return text.trim().split(/\s+/).filter(w => w).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error(t('forum.newPostPage.errorTitleRequired'));
      return;
    }
    
    if (!content.trim()) {
      toast.error(t('forum.newPostPage.errorContentRequired'));
      return;
    }
    
    if (categoryId === 'languages' && !languageCode) {
      toast.error(t('forum.newPostPage.errorLanguageRequired'));
      return;
    }
    
    const wordCount = getWordCount(content);
    if (wordCount > 250) {
      toast.error(t('forum.newPostPage.errorTooLong', { count: wordCount }));
      return;
    }
    
    try {
      setSubmitting(true);
      const postData = {
        category: categoryId,
        title: title.trim(),
        content: content.trim()
      };
      
      if (categoryId === 'languages') {
        postData.language_code = languageCode;
        postData.language_name = languageName;
      }
      
      const response = await api.post('/forum/post', postData);
      toast.success(t('forum.newPostPage.postCreated'));
      navigate(`/community/forum/post/${response.data.post_id}`);
    } catch (err) {
      console.error('Failed to create post:', err);
      toast.error(err.response?.data?.detail || t('forum.newPostPage.createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          {t('forum.newPostPage.back')}
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">{t('forum.newPostPage.createNewPost')}</h1>
          <p className="text-slate-400">
            {t('forum.newPostPage.postIn', { category: categoryNames[categoryId] })}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Language Selector (for languages category) */}
          {categoryId === 'languages' && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                <Globe className="w-4 h-4 inline mr-2" />
                {t('forum.newPostPage.language')} *
              </label>
              <select
                value={languageCode}
                onChange={(e) => {
                  const selected = languages.find(l => l.code === e.target.value);
                  setLanguageCode(e.target.value);
                  setLanguageName(selected?.name || e.target.value);
                }}
                className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-amber-500"
                required
              >
                <option value="">{t('forum.newPostPage.selectLanguage')}</option>
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {t('forum.newPostPage.titleQuestion')} *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 250))}
              placeholder={t('forum.newPostPage.titlePlaceholder')}
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              maxLength={250}
              required
            />
            <p className="text-xs text-slate-500 mt-1 text-right">
              {t('forum.newPostPage.characters', { count: title.length })}
            </p>
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {t('forum.newPostPage.details')} *
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t('forum.newPostPage.detailsPlaceholder')}
              rows={8}
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 resize-none"
              required
            />
            <p className={`text-xs mt-1 text-right ${getWordCount(content) > 250 ? 'text-red-400' : 'text-slate-500'}`}>
              {t('forum.newPostPage.wordCount', { count: getWordCount(content) })}
            </p>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-3 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-medium transition-colors"
            >
              {t('forum.newPostPage.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting || getWordCount(content) > 250}
              className="flex items-center gap-2 px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium transition-colors"
              data-testid="submit-post-btn"
            >
              <Send className="w-5 h-5" />
              {submitting ? t('forum.newPostPage.creating') : t('forum.newPostPage.createPost')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForumNewPost;
